/**
 * ErrorBoundary — catches unhandled React errors in child components
 * and renders a friendly fallback UI.
 *
 * Technical details are only shown in development mode.
 * Errors are logged through the analytics utility.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { emitAnalyticsEvent } from '../lib/analytics';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback UI; defaults to a built-in friendly layout */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    emitAnalyticsEvent('Unhandled Error', {
      message: error.message,
      componentStack: info.componentStack,
    });
  }

  private handleRefresh = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const isDev = import.meta.env?.DEV ?? false;

    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6">
        <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900/80 p-8 shadow-2xl text-center space-y-6">
          <div className="text-5xl">⚠️</div>
          <h1 className="text-2xl font-extrabold text-white">Something went wrong</h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            An unexpected error occurred. Please try refreshing the page, or return to the dashboard
            to continue.
          </p>
          {isDev && this.state.error && (
            <div className="rounded-xl border border-red-800/50 bg-red-950/20 p-4 text-left">
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-400 mb-2">
                Development Error Details
              </p>
              <pre className="text-xs text-red-300 font-mono whitespace-pre-wrap overflow-auto max-h-40">
                {this.state.error.message}
              </pre>
              {this.state.error.stack && (
                <pre className="mt-2 text-[10px] text-red-400/70 font-mono whitespace-pre-wrap overflow-auto max-h-32">
                  {this.state.error.stack}
                </pre>
              )}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={this.handleRefresh}
              className="flex-1 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-500 transition"
            >
              Refresh Page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="flex-1 rounded-xl border border-slate-700 py-3 text-sm font-semibold text-slate-300 hover:bg-slate-800 transition"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
